/**
 * PF Revamp — schema version sentinels.
 *
 * `PF_VERSION` is written into every `OrderDraftDTO` (field `pfVersion`) on
 * save and checked on read. A mismatch means the persisted draft was created
 * by a different version of the FE whose cart-item shapes, config DTOs, or
 * instruction fields may be incompatible with the current hydration logic.
 *
 * `CURRENT_SCHEMA_VERSION` guards against forward-compatibility: if a draft
 * carries a higher `schemaVersion` than this client supports, reading it would
 * silently truncate v2 multi-property buckets (F-A8). We return null and log
 * instead of corrupting data.
 *
 * Consumed by:
 *   - `LocalDraftStore.get` / `LocalDraftStore.list` — validation / filtering
 *   - `serializeDraft` (via re-export in `store/pfVersion.ts`) — written on save
 *
 * History:
 *   1 — initial version (2026-04-30)
 *   2 — `productConfigs` keyed by `"${cartItemId}|${flowId}"` instead of bare
 *       `cartItemId` (phase 1.6 / F-A2 / F-O3). Breaking: drafts from version 1
 *       have configs under the bare `cartItemId` key and will not hydrate
 *       correctly. No migration path: v1 productConfigs shape is irreconcilable
 *       without knowing which flowId maps to each bare key. Invalidated.
 *   3 — single-property collapse (D13): property / items / configs /
 *       configsUserConfirmed moved from a single-element `properties[]` bucket
 *       to the DTO root. Breaking, but deterministically migratable (only ever
 *       one bucket) — see `PF_VERSION_MIGRATIONS[2]`.
 *   4 — catalogue shell ids re-keyed onto the BE `productGroupCode`
 *       (A1.1 / ISSUE-2231, 2026-07-29). `items[].shellProductId` is
 *       FE-synthetic (`shell_<group>[_<category>]`, previously
 *       `shell_<category>_<kind>`) and is persisted verbatim by
 *       `serializeDraft`, so every v3 draft carries ids that no longer join the
 *       catalogue: cart rows would render as "Unknown product", the edit drawer
 *       would open empty, and `scheduledByCT` / instructions-skip defaults would
 *       flip. No migration: the old ids cannot be mapped without the
 *       pre-change catalogue. v3 drafts are invalidated (belt: the resume path
 *       also drops unresolvable items with the `drafts.resume_stale_items`
 *       toast — see `ContinueOrderDialog`).
 *   5 — On-Site Area Report configuration rebuilt onto the redesigned model
 *       (2026-09-09). The measurement draft carried a flat
 *       `numberOfResidentialUnits` count and no scopes; it now carries a
 *       per-unit `residentialUnits[]` and `cellarScope` / `atticScope`. No
 *       migration: a v4 draft records how many units and how many floors in
 *       total, never how the floors were distributed between the units, and the
 *       engine prices per unit — so a resumed draft would quote and bill an
 *       arrangement the client never described. Measurement is the only flow
 *       affected; the version is global, so all v4 drafts are invalidated.
 *
 * schemaVersion history:
 *   1 — initial value. The order is scoped to a single property. (Multi-property
 *       was never built; the v3 collapse makes single-property the locked shape.)
 *
 * **Bump policy (F-A30):**
 *   - Bump `PF_VERSION` ONLY when `hydrateFromDraft` cannot safely handle a
 *     draft written by the previous version (true breaking change).
 *   - For additive fields: do NOT bump — add `?? defaultValue` in
 *     `hydrateFromDraft` so existing drafts hydrate gracefully.
 *   - For breaking changes that have a deterministic migration path: bump AND
 *     add an entry to `PF_VERSION_MIGRATIONS[fromVersion]`.
 *   - For breaking changes that cannot be migrated (e.g. the 1→2 keying
 *     change): bump and accept that existing drafts are invalidated. Document
 *     the reason in the History block above.
 */

import type { OrderDraftDTO } from 'models/purchaseFlowRevamp'

export const PF_VERSION = 5

/**
 * The pfVersion-2 draft shape, as far as the 2→3 migration needs it: property /
 * items / configs / configsUserConfirmed nested under a single-element
 * `properties[]` bucket. (Earlier versions used the same array; v3 flattens it.)
 */
type LegacyDraftV2 = Omit<OrderDraftDTO, 'property' | 'items' | 'configs' | 'configsUserConfirmed'> & {
  properties?: Array<Pick<OrderDraftDTO, 'property' | 'items' | 'configs' | 'configsUserConfirmed'>>
}

/**
 * Migration functions keyed by the **source** `pfVersion` they upgrade FROM.
 *
 * When `LocalDraftStore.get()` encounters a draft with `pfVersion < PF_VERSION`,
 * it walks this map from `draft.pfVersion` up to `PF_VERSION - 1`, applying
 * each migration in sequence. If any version in the chain is missing from this
 * map, the draft is considered unrecoverable and is dropped (null + log).
 *
 * **Adding a migration:**
 * 1. Bump `PF_VERSION` (e.g. from 3 to 4).
 * 2. Add an entry `[3]: (old) => { ... return upgradedDto }` here.
 * 3. The function receives `unknown` because the old DTO shape may differ
 *    significantly — use defensive casts and `?? defaults`.
 * 4. The return value must be a valid `OrderDraftDTO` at the NEW version.
 *
 * No 1→2 migration exists — the 1→2 keying change (productConfigs keys) is not
 * recoverable without knowing the original flowId, so v1 drafts are dropped.
 * The 2→3 collapse (D13) IS deterministic and is migrated below.
 *
 * ⚠️ There is deliberately **no 3→4 entry** (A1.1 shell-id re-key — see the
 * History block): old `items[].shellProductId`s cannot be mapped onto the new
 * group-derived ids. Consequence: the chain can no longer reach the current
 * version from ANY older version, so v1/v2/v3 drafts are all dropped and the
 * 2→3 function below is currently unreachable via `get()`. It is kept (and
 * unit-tested directly) so the collapse stays available if a 3→4 path is ever
 * added.
 *
 * @example
 * // Upgrading a hypothetical v3 → v4 field rename:
 * [3]: (old: unknown) => {
 *   const prev = old as OrderDraftDTO & { legacyField?: string }
 *   return { ...prev, pfVersion: 4, newField: prev.legacyField ?? '' }
 * }
 */
export const PF_VERSION_MIGRATIONS: Record<number, (oldDraft: unknown) => OrderDraftDTO> = {
  // 2 → 3: collapse the single-element `properties[]` bucket onto the DTO root
  // (D13). pfVersion-2 nested property / items / configs / configsUserConfirmed
  // under `properties[0]`; v3 hoists them. Deterministic — there is only ever
  // one bucket. A missing/empty array degrades to an empty cart, not a crash.
  [2]: (oldDraft: unknown): OrderDraftDTO => {
    const { properties, ...rest } = oldDraft as LegacyDraftV2
    const bucket = properties?.[0]
    return {
      ...rest,
      pfVersion: 3,
      property: bucket?.property,
      items: bucket?.items ?? [],
      configs: bucket?.configs ?? {},
      configsUserConfirmed: bucket?.configsUserConfirmed,
    }
  },

}

/**
 * Maximum `OrderDraftDTO.schemaVersion` this client can safely hydrate.
 *
 * If a persisted draft carries a higher value, it was written by a newer client
 * that understands an extended shape this client doesn't model. Hydrating it
 * would silently drop the unknown fields — return null instead (F-A8).
 */
export const CURRENT_SCHEMA_VERSION = 1
